/**
 * SEO route manifest — single source of truth for:
 *   - The sitemap (only `type: "indexable"` entries appear)
 *   - The prerender step (every entry is prerendered)
 *   - Per-route validation rules
 *
 * Route types:
 *   - "indexable": public SEO page. Requires H1, title, description, canonical,
 *     og+twitter set, robots=index,follow.
 *   - "funnel": ad-funnel or lead-funnel/thank-you page. Requires title,
 *     robots=noindex,follow. NO canonical, NO public OG, NO structured data.
 *   - "private": admin or auth route. Requires robots=noindex,nofollow,noarchive.
 *     NO canonical, NO OG, NO public schema. H1/description optional.
 *   - "notfound": the 404 page. robots=noindex,follow. NO canonical, NO OG.
 */
import { ARTICLES } from "../../data/articles.mjs";

const SITE = "https://swipetronics.com";

const staticRoutes = [
  // ── Indexable public pages ────────────────────────────────────────────────
  {
    path: "/",
    type: "indexable",
    title: "Merchant Processing & POS Systems | Swipetronics",
    description:
      "Swipetronics provides merchant services, payment processing, POS systems, terminals, and cash discount programs for businesses nationwide.",
    priority: "1.0",
    changefreq: "weekly",
  },
  {
    path: "/services",
    type: "indexable",
    title: "Card Processing & Merchant Services | Swipetronics",
    description:
      "Card processing, mobile payments, digital wallets, ACH, and virtual terminals — one provider for every way your customers want to pay.",
    priority: "0.9",
    changefreq: "monthly",
  },
  {
    path: "/products",
    type: "indexable",
    title: "Payment Terminals & Card Readers | Swipetronics",
    description:
      "Countertop, wireless, and smart payment terminals with EMV chip, tap-to-pay, and receipt printing — built for reliable everyday checkout.",
    priority: "0.9",
    changefreq: "monthly",
  },
  {
    path: "/pos-systems",
    type: "indexable",
    title: "POS Systems for Restaurants & Retail | Swipetronics",
    description:
      "Modern all-in-one POS systems with inventory, tips, split checks, and reporting — built for restaurants, retail, and service businesses.",
    priority: "0.9",
    changefreq: "monthly",
  },
  {
    path: "/mobile-devices",
    type: "indexable",
    title: "Mobile Payment Devices & Handheld POS | Swipetronics",
    description:
      "Accept payments anywhere with mobile POS devices — real-time transactions, invoicing by email or SMS, and full reporting from your phone.",
    priority: "0.8",
    changefreq: "monthly",
  },
  {
    path: "/e-commerce",
    type: "indexable",
    title: "E-Commerce & Online Payment Processing | Swipetronics",
    description:
      "Accept online payments with secure gateways, tokenized card storage, and API integrations for your store or platform.",
    priority: "0.8",
    changefreq: "monthly",
  },
  {
    path: "/cash-discount",
    type: "indexable",
    title: "Cash Discount Program for Merchants | Swipetronics",
    description:
      "See how eligible businesses can reduce card-processing costs with a properly configured cash discount program and clear customer disclosures.",
    priority: "0.8",
    changefreq: "monthly",
  },
  {
    path: "/free-pos-program",
    type: "indexable",
    title: "Free POS Program — Up to $3,000 in Equipment | Swipetronics",
    description:
      "Eligible businesses may qualify for up to $3,000 in free POS equipment — modern checkout tools with no major upfront purchase.",
    priority: "0.8",
    changefreq: "monthly",
  },
  {
    path: "/industries",
    type: "indexable",
    title: "Payment Processing by Industry | Swipetronics",
    description:
      "Payment processing built for restaurants, retail, delis, convenience stores, salons, service businesses, e-commerce, and more.",
    priority: "0.7",
    changefreq: "monthly",
  },
  {
    path: "/partnerships",
    type: "indexable",
    title: "Merchant Services Partnerships & ISO Program | Swipetronics",
    description:
      "Partner with Swipetronics — ISO and agent partnership opportunities with modern POS and processing tools to grow your merchant book.",
    priority: "0.7",
    changefreq: "monthly",
  },
  {
    path: "/resources",
    type: "indexable",
    title: "Blog & Merchant Processing Resources | Swipetronics",
    description:
      "Straight-talk guides on merchant statements, cash discount, dual pricing, and switching processors — written for real business owners.",
    priority: "0.7",
    changefreq: "weekly",
  },
  {
    path: "/resources/faq",
    type: "indexable",
    title: "Merchant Account FAQs | Swipetronics",
    description:
      "Answers to common questions about merchant accounts, setup, approvals, statements, deposits, and support for card processing.",
    priority: "0.7",
    changefreq: "monthly",
  },
  {
    path: "/about",
    type: "indexable",
    title: "About Swipetronics — Merchant Services for Business Owners",
    description:
      "Swipetronics helps businesses reduce unnecessary processing costs, upgrade payment technology, and receive dependable merchant support.",
    priority: "0.6",
    changefreq: "monthly",
  },
  {
    path: "/contact",
    type: "indexable",
    title: "Contact Swipetronics — Sales & Support",
    description:
      "Talk to Swipetronics about your merchant processing, POS systems, or a free savings analysis. Call, email, or send a message.",
    priority: "0.6",
    changefreq: "monthly",
  },
  {
    path: "/privacy",
    type: "indexable",
    title: "Privacy Policy | Swipetronics",
    description:
      "How Swipetronics collects, uses, and protects information gathered through our website, merchant applications, and customer support.",
    priority: "0.3",
    changefreq: "yearly",
  },
  {
    path: "/terms",
    type: "indexable",
    title: "Terms of Service | Swipetronics",
    description:
      "The terms that govern use of the Swipetronics website, merchant application process, and related services.",
    priority: "0.3",
    changefreq: "yearly",
  },
  {
    path: "/merchant-services-syracuse-ny",
    type: "indexable",
    title: "Merchant Services & Card Processing Syracuse NY | Swipetronics",
    description:
      "Swipetronics provides merchant services, card processing, POS systems, payment terminals, and cash discount solutions for businesses in Syracuse, NY.",
    priority: "0.8",
    changefreq: "monthly",
  },

  // ── Industry-specific SEO landing pages ─────────────────────────────
  {
    path: "/industries/restaurants",
    type: "indexable",
    title: "Restaurant POS & Card Processing | Swipetronics",
    description:
      "Card processing, tipping, and POS setups for restaurants — fast checkout, split checks, and tip handling with terminals built for the rush.",
    priority: "0.75",
    changefreq: "monthly",
  },
  {
    path: "/industries/convenience-stores-delis",
    type: "indexable",
    title: "Convenience Store & Deli POS + Card Processing | Swipetronics",
    description:
      "Card processing and POS setups for convenience stores and delis — fast scanning, tap-to-pay, and terminal options built for high transaction volume.",
    priority: "0.75",
    changefreq: "monthly",
  },
  {
    path: "/industries/grocery-stores",
    type: "indexable",
    title: "Grocery Store Card Processing & POS | Swipetronics",
    description:
      "Card processing and POS setups for grocery stores — EBT acceptance on select terminals, tap-to-pay, and equipment options for single- or multi-lane checkout.",
    priority: "0.75",
    changefreq: "monthly",
  },
  {
    path: "/industries/smoke-shops",
    type: "indexable",
    title: "Smoke Shop POS & Card Processing | Swipetronics",
    description:
      "Card processing, POS options, and Cash Discount setups for eligible smoke shops. Payment terminals with tap, chip, and swipe on select models.",
    priority: "0.75",
    changefreq: "monthly",
  },
  {
    path: "/industries/retail-stores",
    type: "indexable",
    title: "Retail Store POS & Card Processing | Swipetronics",
    description:
      "Card processing and POS setups for retail stores — tap-to-pay, receipt options, and terminal choices for boutique, apparel, and specialty retail.",
    priority: "0.75",
    changefreq: "monthly",
  },
  {
    path: "/industries/salons-spas",
    type: "indexable",
    title: "Salon & Spa POS + Card Processing | Swipetronics",
    description:
      "Card processing and POS setups for salons and spas — tip handling, clean receipts, and terminal options built for service-based businesses.",
    priority: "0.75",
    changefreq: "monthly",
  },
  {
    path: "/industries/service-businesses",
    type: "indexable",
    title: "Service Business Card Processing & Mobile Payments | Swipetronics",
    description:
      "Card processing, mobile payments, and invoicing options for service businesses — in-the-field payments, virtual terminal, and recurring billing setups.",
    priority: "0.75",
    changefreq: "monthly",
  },

  // ── Funnel / thank-you (noindex, follow) ─────────────────────────────────
  {
    path: "/free-pos-offer",
    type: "funnel",
    title: "Free POS Offer | Swipetronics",
  },
  {
    path: "/cash-discount-savings",
    type: "funnel",
    title: "Cash Discount Savings | Swipetronics",
  },
  {
    path: "/savings-analysis",
    type: "funnel",
    title: "Free Savings Analysis | Swipetronics",
  },
  {
    path: "/get-started",
    type: "funnel",
    title: "Get Started | Swipetronics",
  },

  // ── Private / admin (noindex, nofollow, noarchive) ───────────────────────
  { path: "/admin", type: "private", title: "Admin — Swipetronics" },
  { path: "/admin/login", type: "private", title: "Admin Login — Swipetronics" },
  { path: "/auth/callback", type: "private", title: "Signing in — Swipetronics" },

  // ── NotFound (baked as build/404.html by build-404 script) ───────────────
  { path: "/404", type: "notfound", title: "Page Not Found | Swipetronics" },
];

// Blog articles are dynamically appended from the ARTICLES data source.
const articleRoutes = ARTICLES.map((a) => ({
  path: `/resources/${a.slug}`,
  type: "indexable",
  title: a.seoTitle,
  description: a.seoDescription,
  priority: "0.6",
  changefreq: "monthly",
  ogType: "article",
}));

export const SEO_ROUTES = [...staticRoutes, ...articleRoutes];
export const SITE_URL = SITE;

// Convenience: indexable-only list for sitemap.
export const indexableRoutes = () =>
  SEO_ROUTES.filter((r) => r.type === "indexable");

// Convenience: lookup SEO props for a given path. Returns null if not found.
// For indexable routes, also derives the canonical from SITE_URL + path.
export function getSeoForPath(path) {
  const route = SEO_ROUTES.find((r) => r.path === path);
  if (!route) return null;
  if (route.type === "indexable") {
    return {
      title: route.title,
      description: route.description,
      canonical: `${SITE_URL}${path}`,
      ogType: route.ogType || "website",
    };
  }
  if (route.type === "funnel") {
    return { title: route.title, noindex: true };
  }
  if (route.type === "private") {
    return { title: route.title, private: true };
  }
  if (route.type === "notfound") {
    return { title: route.title, notFound: true };
  }
  return null;
}
